import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, NavLink } from 'react-router-dom';
import { Zap, Shield, FileText, Newspaper, LogOut, KeyRound, User, BarChart2, Star, Images } from 'lucide-react';
import NewsPage from './pages/NewsPage';
import ReportsPage from './pages/ReportsPage';
import AdminPage from './pages/AdminPage';
import ArchivePage from './pages/ArchivePage';
import PressPage from './pages/PressPage';
import CardNewsPage from './pages/CardNewsPage';
import BigKindsPage from './pages/BigKindsPage';
import MediaStatsPage from './pages/MediaStatsPage';
import MediaEvaluationPage from './pages/MediaEvaluationPage';
import LoginPage from './pages/LoginPage';
import { AuthProvider, useAuth } from './AuthContext';
import { changePassword } from './api';
import './App.css';

function ChangePwModal({ onClose }) {
  const [cur, setCur] = useState('');
  const [nw, setNw] = useState('');
  const [nw2, setNw2] = useState('');
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (nw !== nw2) return setMsg('새 비밀번호가 일치하지 않습니다.');
    if (nw.length < 6) return setMsg('비밀번호는 6자 이상이어야 합니다.');
    setLoading(true);
    try {
      await changePassword(cur, nw);
      setMsg('✅ 비밀번호가 변경되었습니다.');
      setTimeout(onClose, 1200);
    } catch (err) {
      setMsg(`❌ ${err.response?.data?.error || '오류 발생'}`);
    } finally { setLoading(false); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div className="card" style={{ width: '320px' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 700, color: '#f1f5f9', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <KeyRound size={15} color="#38bdf8" /> 비밀번호 변경
        </h2>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          <input className="input" type="password" placeholder="현재 비밀번호" value={cur} onChange={e => setCur(e.target.value)} required style={{ width: '100%', boxSizing: 'border-box' }} />
          <input className="input" type="password" placeholder="새 비밀번호 (6자 이상)" value={nw} onChange={e => setNw(e.target.value)} required style={{ width: '100%', boxSizing: 'border-box' }} />
          <input className="input" type="password" placeholder="새 비밀번호 확인" value={nw2} onChange={e => setNw2(e.target.value)} required style={{ width: '100%', boxSizing: 'border-box' }} />
          {msg && <div style={{ fontSize: '0.8rem', color: msg.startsWith('✅') ? '#4ade80' : '#f87171' }}>{msg}</div>}
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem' }}>
            <button className="btn btn-primary btn-sm" type="submit" disabled={loading} style={{ flex: 1 }}>{loading ? '처리 중...' : '변경'}</button>
            <button className="btn btn-sm" type="button" onClick={onClose} style={{ flex: 1, background: '#1e293b', color: '#94a3b8', border: '1px solid #334155' }}>취소</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AppInner() {
  const { user, loading, logout } = useAuth();
  const [showChangePw, setShowChangePw] = useState(false);
  const [showForcePw, setShowForcePw] = useState(false);
  const [navOpen, setNavOpen] = useState(false);

  React.useEffect(() => {
    if (user?.must_change_password) setShowForcePw(true);
  }, [user]);

  if (loading) return <div style={{ minHeight: '100vh', background: '#020817', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569' }}>로딩 중...</div>;
  if (!user) return <LoginPage />;

  const isMaster = user.role === 'master';

  return (
    <div className="app">
      {showForcePw && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div className="card" style={{ width: '340px' }}>
            <h2 style={{ fontSize: '1rem', fontWeight: 700, color: '#f1f5f9', marginBottom: '0.5rem' }}>🔐 비밀번호 변경 필요</h2>
            <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '1rem' }}>최초 로그인 시 비밀번호를 반드시 변경해야 합니다.</p>
            <ChangePwModal onClose={() => { setShowForcePw(false); }} />
          </div>
        </div>
      )}
      {showChangePw && <ChangePwModal onClose={() => setShowChangePw(false)} />}

      <header className="header">
        <div className="header-inner">
          <div className="logo">
            <Zap size={22} className="logo-icon" />
            <span>에너지·원자력 뉴스 모니터</span>
          </div>

          {/* 데스크톱 네비 */}
          <nav className="nav">
            <NavLink to="/press" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'} onClick={() => setNavOpen(false)}>
              <Newspaper size={16} /> 보도자료
            </NavLink>
            <NavLink to="/card-news" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'} onClick={() => setNavOpen(false)}>
              <Images size={16} /> 카드뉴스
            </NavLink>
            <NavLink to="/reports" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'} onClick={() => setNavOpen(false)}>
              <FileText size={16} /> 리포트
            </NavLink>
            <NavLink to="/media-stats" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'} onClick={() => setNavOpen(false)}>
              <BarChart2 size={16} /> 보도 통계
            </NavLink>
            <NavLink to="/media-evaluation" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'} onClick={() => setNavOpen(false)}>
              <Star size={16} /> 매체 평가
            </NavLink>
            {isMaster && (
              <NavLink to="/admin" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'} onClick={() => setNavOpen(false)}>
                <Shield size={16} /> 관리자
              </NavLink>
            )}
          </nav>

          {/* 데스크톱 사용자 액션 */}
          <div className="header-user-actions" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: '0.75rem' }}>
            <span style={{ fontSize: '0.75rem', color: '#475569', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <User size={12} /> {user.username}
              {isMaster && <span style={{ fontSize: '0.6rem', background: '#1d4ed8', color: '#fff', padding: '1px 5px', borderRadius: '3px', marginLeft: '3px' }}>MASTER</span>}
            </span>
            <button onClick={() => setShowChangePw(true)} title="비밀번호 변경"
              style={{ background: 'none', border: '1px solid #1e293b', borderRadius: '5px', color: '#475569', cursor: 'pointer', padding: '3px 6px', fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
              <KeyRound size={11} />
            </button>
            <button onClick={logout} title="로그아웃"
              style={{ background: 'none', border: '1px solid #1e293b', borderRadius: '5px', color: '#475569', cursor: 'pointer', padding: '3px 6px', fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
              <LogOut size={11} />
            </button>
          </div>

          {/* 모바일 햄버거 버튼 */}
          <button className="hamburger" onClick={() => setNavOpen(o => !o)} aria-label="메뉴">
            <span style={{ transform: navOpen ? 'rotate(45deg) translate(5px, 5px)' : 'none' }} />
            <span style={{ opacity: navOpen ? 0 : 1 }} />
            <span style={{ transform: navOpen ? 'rotate(-45deg) translate(5px, -5px)' : 'none' }} />
          </button>
        </div>

        {/* 모바일 네비 드로어 */}
        <nav className={`nav-drawer${navOpen ? ' open' : ''}`}>
          <NavLink to="/press" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'} onClick={() => setNavOpen(false)}>
            <Newspaper size={16} /> 보도자료
          </NavLink>
          <NavLink to="/card-news" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'} onClick={() => setNavOpen(false)}>
            <Images size={16} /> 카드뉴스
          </NavLink>
          <NavLink to="/reports" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'} onClick={() => setNavOpen(false)}>
            <FileText size={16} /> 리포트
          </NavLink>
          <NavLink to="/media-stats" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'} onClick={() => setNavOpen(false)}>
            <BarChart2 size={16} /> 보도 통계
          </NavLink>
          <NavLink to="/media-evaluation" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'} onClick={() => setNavOpen(false)}>
            <Star size={16} /> 매체 평가
          </NavLink>
          {isMaster && (
            <NavLink to="/admin" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'} onClick={() => setNavOpen(false)}>
              <Shield size={16} /> 관리자
            </NavLink>
          )}
          <hr className="nav-drawer-divider" />
          <div className="nav-drawer-user">
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              <User size={13} /> {user.username}
              {isMaster && <span style={{ fontSize: '0.6rem', background: '#1d4ed8', color: '#fff', padding: '1px 5px', borderRadius: '3px' }}>MASTER</span>}
            </span>
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              <button onClick={() => { setShowChangePw(true); setNavOpen(false); }}
                style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '5px', color: '#94a3b8', cursor: 'pointer', padding: '4px 8px', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                <KeyRound size={12} /> 비밀번호
              </button>
              <button onClick={() => { logout(); setNavOpen(false); }}
                style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '5px', color: '#94a3b8', cursor: 'pointer', padding: '4px 8px', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                <LogOut size={12} /> 로그아웃
              </button>
            </div>
          </div>
        </nav>
      </header>

      <main className="main">
        <Routes>
          <Route path="/" element={<NewsPage isAdmin={isMaster} />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/report/:id" element={<ReportsPage />} />
          <Route path="/archive" element={<ArchivePage />} />
          <Route path="/press" element={<PressPage />} />
          <Route path="/card-news" element={<CardNewsPage />} />
          <Route path="/bigkinds" element={<BigKindsPage />} />
          <Route path="/media-stats" element={<MediaStatsPage />} />
          <Route path="/media-evaluation" element={<MediaEvaluationPage />} />
          {isMaster && <Route path="/admin" element={<AdminPage />} />}
        </Routes>
      </main>

      <footer className="footer">
        <span>에너지·원자력 뉴스 모니터 &copy; {new Date().getFullYear()}</span>
      </footer>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppInner />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
